import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Client, CreateClientForm, UpdateClientForm } from '../../types';
import PhoneInput from './PhoneInput';

interface ClientFormProps {
	client?: Client | null;
	onSubmit: (data: CreateClientForm | UpdateClientForm) => void;
	isLoading?: boolean;
	error?: string;
}

const clientSchema = yup.object({
	name: yup.string().required('Name is required').max(100, 'Name must be less than 100 characters'),
	phone: yup.string().max(50, 'Phone must be less than 50 characters'),
	email: yup.string().email('Please enter a valid email address').max(100, 'Email must be less than 100 characters'),
	rank: yup.mixed<number | null>()
		.nullable()
		.transform((value, originalValue) => {
			// Convert empty string, null, undefined, or NaN to null
			if (originalValue === '' || originalValue === null || originalValue === undefined) {
				return null;
			}
			const numValue = Number(originalValue);
			if (isNaN(numValue)) {
				return null;
			}
			return numValue;
		})
		.test('is-valid-number', 'Rank must be a number between 0 and 100', function(value) {
			// If value is null/undefined, it's valid (optional field)
			if (value === null || value === undefined) {
				return true;
			}
			// If it's a number, validate range
			return typeof value === 'number' && value >= 0 && value <= 100;
		})
}).required();

const ClientForm: React.FC<ClientFormProps> = ({ client, onSubmit, isLoading = false, error }) => {
	const {
		register,
		handleSubmit,
		control,
		formState: { errors },
		reset
	} = useForm<CreateClientForm | UpdateClientForm>({
		resolver: yupResolver(clientSchema),
		defaultValues: {
			name: client?.name || '',
			phone: client?.phone || '',
			email: client?.email || '',
			rank: client?.rank || undefined
		}
	});

	const handleFormSubmit = (data: CreateClientForm | UpdateClientForm) => {
		console.log('ClientForm: Form submitted with data:', data);
		console.log('ClientForm: onSubmit function:', onSubmit);
		
		// Convert empty strings to null/undefined for optional fields
		const formData = {
			...data,
			phone: data.phone && data.phone.trim() ? data.phone.trim() : undefined,
			email: data.email && data.email.trim() ? data.email.trim() : undefined,
			rank: data.rank ?? 0
		};
		
		onSubmit(formData);
	};

	// Reset form when client prop changes
	React.useEffect(() => {
		if (client) {
			reset({
				name: client.name,
				phone: client.phone || '',
				email: client.email || '',
				rank: client.rank || undefined
			});
		} else {
			reset({
				name: '',
				phone: '',
				email: '',
				rank: undefined
			});
		}
	}, [client, reset]);

	return (
		<form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
			{error && (
				<div className="rounded-md bg-red-50 p-4">
					<div className="flex">
						<div className="ml-3">
							<h3 className="text-sm font-medium text-red-800">Error</h3>
							<div className="mt-2 text-sm text-red-700">{error}</div>
						</div>
					</div>
				</div>
			)}

			{/* Note about location management */}
			{!client && (
				<div className="rounded-md bg-accent-color-50 p-4">
					<div className="flex">
						<div className="ml-3">
							<h3 className="text-sm font-medium text-accent-color-800">Location Management</h3>
							<div className="mt-2 text-sm text-accent-color-700">
								After creating the client, you can add and manage locations in the client details page.
							</div>
						</div>
					</div>
				</div>
			)}

			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				{/* Name */}
				<div className="md:col-span-2">
					<label htmlFor="name" className="block text-sm font-medium text-gray-700">
						Name <span className="text-red-500">*</span>
					</label>
					<input
						{...register('name')}
						type="text"
						id="name"
						className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
						placeholder="Enter client name"
					/>
					{errors.name && (
						<p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
					)}
				</div>

				{/* Phone */}
				<div>
					<label htmlFor="phone" className="block text-sm font-medium text-gray-700">
						Phone
					</label>
					<Controller
						name="phone"
						control={control}
						render={({ field }) => (
							<PhoneInput
								value={field.value || ''}
								onChange={field.onChange}
								className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
								placeholder="Enter phone number"
								id="phone"
								name="phone"
							/>
						)}
					/>
					{errors.phone && (
						<p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
					)}
				</div>

				{/* Email */}
				<div>
					<label htmlFor="email" className="block text-sm font-medium text-gray-700">
						Email
					</label>
					<input
						{...register('email')}
						type="email"
						id="email"
						className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
						placeholder="Enter email address"
					/>
					{errors.email && (
						<p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
					)}
				</div>

				{/* Rank */}
				<div>
					<label htmlFor="rank" className="block text-sm font-medium text-gray-700">
						Rank
					</label>
					<input
						{...register('rank', { 
							setValueAs: (value) => {
								// Handle empty string, null, undefined, or NaN - return empty string to let yup transform handle it
								if (value === '' || value === null || value === undefined) {
									return '';
								}
								const numValue = Number(value);
								if (isNaN(numValue)) {
									return '';
								}
								return numValue;
							}
						})}
						type="number"
						id="rank"
						min="0"
						max="100"
						className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
						placeholder="Enter rank (0-100, optional)"
					/>
					{errors.rank && (
						<p className="mt-1 text-sm text-red-600">{errors.rank.message}</p>
					)}
				</div>
			</div>

			<div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
				<button
					type="submit"
					disabled={isLoading}
					className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{isLoading ? (
						<div className="flex items-center">
							<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
							{client ? 'Updating...' : 'Creating...'}
						</div>
					) : (
						client ? 'Update Client' : 'Create Client'
					)}
				</button>
			</div>
		</form>
	);
};

export default ClientForm;
